import React from 'react';
import { useAct, gsap, DEBUG, prefersReduced } from '../hooks/useScrollStory';

/* ══════════════════════════════════════════════════════
   RAKAMLAR — sahadaki büyüklük (21st.dev tarzı kartlar)
   Sayılar piyango/slot makinesi gibi geliyor: her basamak
   0-9 arasında dönüyor ve soldan sağa teker teker yerine
   oturuyor (odometre efekti). Bir kez oynuyor (scrub değil).

   Altta fotoğraf için hazır 16:9 yuva var (.photo-slot);
   görseli koymak için içindeki <img>'in yorumunu kaldır.
   ══════════════════════════════════════════════════════ */

const STATS = [
  {
    value: 20000, suffix: '+', label: 'Aktif kullanıcı',
    sub: 'Her gün tarlasını Agren ile kontrol ediyor',
    icon: (
      <>
        <path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="9" cy="7.5" r="3.2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M22 20v-1.4a4 4 0 0 0-3-3.86M16.5 4.4a3.2 3.2 0 0 1 0 6.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </>
    ),
  },
  {
    value: 200000, suffix: '+', label: 'İzlenen tarla',
    sub: 'Uydu ve yapay zekâ ile sürekli takipte',
    icon: (
      <>
        <path d="M9 4 3 6.2v13.4L9 17.4l6 2.2 6-2.2V4l-6 2.2L9 4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 4v13.4M15 6.2v13.4" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </>
    ),
  },
];

const fmt = (n) => Math.round(n).toLocaleString('tr-TR');

/* Her basamak için 0-9 arası SPINS tur dönen bir makara.
   Makara hedef rakamda duruyor; aradan geçen tüm rakamlar
   dönme hissini veriyor. */
const SPINS = 5;

/* Odometreyi React'le DEĞİL, DOM API ile kuruyoruz. Her basamak
   yüzlerce hücreye açılıyor; bunları React'in yönetmesi commit
   sırasında reconciliation çökmesine yol açıyordu. GSAP'ın serbestçe
   oynatabilmesi için düğümleri React ağacının dışında tutuyoruz. */
function buildOdometer(numEl) {
  const text = numEl.dataset.value || '';
  numEl.textContent = '';
  const odo = document.createElement('span');
  odo.className = 'odo inline-flex items-end';
  [...text].forEach((ch) => {
    if (/\d/.test(ch)) {
      const target = Number(ch);
      const count = SPINS * 10 + target;
      const digit = document.createElement('span');
      digit.className = 'odo-digit relative inline-block overflow-hidden';
      digit.style.height = '1em';
      const strip = document.createElement('span');
      strip.className = 'odo-strip flex flex-col';
      for (let k = 0; k <= count; k++) {
        const cell = document.createElement('span');
        cell.className = 'odo-cell flex items-center justify-center';
        cell.style.height = '1em';
        cell.textContent = String(k % 10);
        strip.appendChild(cell);
      }
      digit.appendChild(strip);
      odo.appendChild(digit);
    } else {
      const sep = document.createElement('span');
      sep.className = 'odo-sep inline-block';
      sep.textContent = ch;
      odo.appendChild(sep);
    }
  });
  numEl.appendChild(odo);
}

export default function Milestones() {
  const ref = useAct((root) => {
    const q = gsap.utils.selector(root);
    q('.m-num').forEach(buildOdometer);
    const cards = q('.m-card');
    const rules = q('.m-rule');

    /* makaranın hedef konumu: son hücreyi pencereye getiren yPercent.
       yüzde kullanıyoruz ki responsive font boyutunda da doğru dursun. */
    const landing = (strip) => {
      const N = strip.children.length;
      return -((N - 1) / N) * 100;
    };

    if (prefersReduced()) {
      q('.odo-strip').forEach((s) => gsap.set(s, { yPercent: landing(s) }));
      gsap.set(rules, { scaleX: 1 });
      gsap.set(cards, { opacity: 1, y: 0 });
      gsap.set(q('.m-kicker, .photo-slot'), { opacity: 1, y: 0 });
      return;
    }

    gsap.set(cards, { opacity: 0, y: 40 });
    gsap.set(rules, { scaleX: 0, transformOrigin: '0% 50%' });
    gsap.set(q('.m-kicker'), { opacity: 0, y: 18 });
    gsap.set(q('.photo-slot'), { opacity: 0, y: 44 });
    gsap.set(q('.odo-strip'), { yPercent: 0 }); // hepsi 0'dan başlıyor

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: 'top 68%',
        toggleActions: 'restart none none reset',
        markers: DEBUG,
      },
    });

    tl.to(q('.m-kicker'), { opacity: 1, y: 0, ease: 'power2.out', duration: 0.55 }, 0);

    cards.forEach((card, i) => {
      const at = 0.2 + i * 0.16;
      tl.to(card, { opacity: 1, y: 0, ease: 'power3.out', duration: 0.8 }, at);
      tl.to(rules[i], { scaleX: 1, ease: 'power2.out', duration: 0.9 }, at + 0.05);

      /* bu kartın basamakları soldan sağa teker teker duruyor */
      const strips = card.querySelectorAll('.odo-strip');
      strips.forEach((strip, di) => {
        tl.to(strip, {
          yPercent: landing(strip),
          duration: 1.05,
          ease: 'power4.out',
        }, at + 0.15 + di * 0.16);
      });
    });

    tl.to(q('.photo-slot'), { opacity: 1, y: 0, ease: 'power2.out', duration: 0.85 }, 0.9);
  }, []);

  return (
    <section
      ref={ref}
      id="rakamlar"
      className="relative overflow-hidden"
      style={{ background: '#050605' }}
    >
      {/* ince nokta ızgara + üstte yumuşak lime parıltı */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,.05) 1px, transparent 1px)',
          backgroundSize: '30px 30px',
          maskImage: 'radial-gradient(ellipse 80% 70% at 50% 30%, #000 0%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 30%, #000 0%, transparent 75%)',
        }} />
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: 'radial-gradient(ellipse 60% 55% at 50% 22%, rgba(200,255,34,.10) 0%, rgba(200,255,34,0) 62%)' }} />

      <div className="relative max-w-[1120px] mx-auto px-6 md:px-10 py-[clamp(96px,16vh,180px)]">
        <div className="m-kicker text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-4 py-1.5 font-mono text-[11.5px] tracking-[.2em] uppercase"
            style={{ color: '#C8FF22' }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#C8FF22', boxShadow: '0 0 10px #C8FF22' }} />
            Sahada
          </span>
          <h2 className="mt-6 font-display font-bold text-white leading-[1.05] tracking-[-0.03em] mx-auto max-w-[16ch]"
            style={{ fontSize: 'clamp(28px,3.6vw,48px)' }}>
            Türkiye'nin tarlaları Agren ile izleniyor
          </h2>
        </div>

        <div className="mt-[clamp(48px,7vh,80px)] grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {STATS.map((s, i) => (
            <div key={s.label}
              className="m-card group relative rounded-[26px] border border-white/10 p-8 md:p-9 overflow-hidden transition-all duration-500 hover:border-white/20 hover:-translate-y-1"
              style={{ background: 'linear-gradient(160deg, rgba(255,255,255,.05) 0%, rgba(255,255,255,.015) 40%, rgba(255,255,255,0) 100%)' }}>

              <div className="absolute top-0 left-0 right-0 h-px" aria-hidden="true">
                <div className="m-rule h-full w-full"
                  style={{ transformOrigin: '0% 50%', background: 'linear-gradient(90deg, rgba(200,255,34,0) 0%, rgba(200,255,34,.9) 18%, rgba(200,255,34,.9) 82%, rgba(200,255,34,0) 100%)' }} />
              </div>
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none opacity-60 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true"
                style={{ background: 'radial-gradient(circle, rgba(200,255,34,.12) 0%, rgba(200,255,34,0) 70%)' }} />

              <div className="relative">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl border border-lime/25"
                  style={{ background: 'rgba(200,255,34,.08)', color: '#C8FF22' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">{s.icon}</svg>
                </div>

                <div className="m-num mt-8 font-display font-bold tabular-nums whitespace-nowrap leading-none tracking-[-0.04em] text-white"
                  data-value={fmt(s.value) + s.suffix}
                  style={{
                    fontSize: 'clamp(40px,4.6vw,72px)',
                    WebkitMaskImage: 'linear-gradient(180deg,#000 0%,#000 46%,rgba(0,0,0,.62) 100%)',
                    maskImage: 'linear-gradient(180deg,#000 0%,#000 46%,rgba(0,0,0,.62) 100%)',
                  }} />

                <div className="mt-5 font-display font-semibold text-white"
                  style={{ fontSize: 'clamp(18px,1.7vw,23px)' }}>
                  {s.label}
                </div>
                <p className="mt-2 text-[14.5px] leading-relaxed text-white/50 max-w-[40ch]">
                  {s.sub}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
